import { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ActionIcon, Anchor, Box, Flex, Image, Paper, Switch, Text, TextInput } from '@mantine/core';
import { IconFile, IconGripVertical, IconTrash } from '@tabler/icons-react';
import { parseYoutubeVideoId, youtubeThumbnailUrl } from '@4each/common';
import type { ChildCollectionConfig } from '../../lib/types';

export interface ChildItem {
  id: number;
  title: string | null;
  file?: string;
  url?: string;
  enabled: boolean | null;
  sort: number | null;
}

interface ChildRowProps {
  item: ChildItem;
  kind: ChildCollectionConfig['kind'];
  onUpdate: (data: Record<string, unknown>) => void;
  onDelete: () => void;
}

export default function ChildRow({ item, kind, onUpdate, onDelete }: ChildRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: String(item.id) });
  const [title, setTitle] = useState(item.title ?? '');

  function saveTitle() {
    if (title !== (item.title ?? '')) onUpdate({ title: title || null });
  }

  return (
    <Paper
      ref={setNodeRef}
      withBorder
      p="xs"
      bg={isDragging ? 'gray.1' : undefined}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <Flex align="center" gap="sm">
        <Box {...attributes} {...listeners} style={{ cursor: isDragging ? 'grabbing' : 'grab', display: 'flex' }}>
          <IconGripVertical size={18} />
        </Box>
        <ChildPreview item={item} kind={kind} />
        <TextInput
          flex={1}
          placeholder="Title"
          value={title}
          onChange={(event) => setTitle(event.currentTarget.value)}
          onBlur={saveTitle}
        />
        <Switch
          label="Enabled"
          checked={item.enabled ?? false}
          onChange={(event) => onUpdate({ enabled: event.currentTarget.checked })}
        />
        <ActionIcon variant="subtle" color="red" onClick={onDelete} aria-label="Delete">
          <IconTrash size={16} />
        </ActionIcon>
      </Flex>
    </Paper>
  );
}

function ChildPreview({ item, kind }: Pick<ChildRowProps, 'item' | 'kind'>) {
  if (kind === 'youtube') {
    const videoId = parseYoutubeVideoId(item.url ?? '');
    return (
      <Anchor href={item.url} target="_blank" rel="noreferrer">
        {videoId ? <Image src={youtubeThumbnailUrl(videoId)} alt="" w={96} h={54} fit="cover" /> : item.url}
      </Anchor>
    );
  }
  if (kind === 'image') {
    return <Image src={`/uploads/${item.file}`} alt="" w={96} h={54} fit="contain" />;
  }
  return (
    <Anchor href={`/uploads/${item.file}`} target="_blank" rel="noreferrer">
      <Flex align="center" gap="xs" maw={240}>
        <IconFile size={18} />
        <Text size="sm" truncate>{item.file}</Text>
      </Flex>
    </Anchor>
  );
}
